import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Save, User, Phone, MapPin, Mail, LogOut } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import UserAvatar from '../components/UserAvatar';

const ProfilePortal = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone_number: '',
    address: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    // Initialize form with user data
    setFormData({
      full_name: user.full_name || '',
      phone_number: user.phone_number || '',
      address: user.address || ''
    });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.put(`${API}/users/me/details`, formData);
      toast.success('Informations mises à jour avec succès!');
      
      // Update local user context would require a refresh or context update
      // For now, just show success
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const getDashboardRoute = () => {
    if (user?.role === 'admin') return '/admin-dashboard';
    if (user?.role === 'doctor') return '/doctor-dashboard';
    return '/patient-dashboard';
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="profile-portal">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(getDashboardRoute())}
                className="flex items-center space-x-1 text-teal-200 hover:text-white transition-colors"
                data-testid="back-dashboard-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm hidden sm:inline">Retour</span>
              </button>
              <img 
                src="https://customer-assets.emergentagent.com/job_babidoc-health/artifacts/7a0rukid_HUMMM0fc84adc-81cc-4751-9a5e-2c9327247823.png" 
                alt="BabiDoc" 
                className="w-10 h-10 object-cover rounded-full"
              />
              <div>
                <h1 className="text-lg font-bold">Mon Portail</h1>
                <p className="text-xs text-teal-100">Gérer mes informations</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <UserAvatar name={user.full_name} role={user.role} size="sm" />
              <Button 
                variant="ghost" 
                onClick={handleLogout} 
                className="text-white hover:bg-teal-700"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Profile Card */}
        <Card className="mb-6">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <UserAvatar name={user.full_name} role={user.role} size="lg" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              {user.full_name}
            </CardTitle>
            <p className="text-gray-500">{user.email}</p>
            <div className="mt-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                user.role === 'doctor' 
                  ? 'bg-green-100 text-green-700' 
                  : user.role === 'admin'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-orange-100 text-orange-700'
              }`}>
                {user.role === 'doctor' ? 'Médecin' : user.role === 'admin' ? 'Administrateur' : 'Patient'}
              </span>
            </div>
          </CardHeader>
        </Card>

        {/* Edit Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-700">
              Modifier mes informations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom complet
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Votre nom complet"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="pl-12 h-12"
                    data-testid="fullname-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                  <span className="text-gray-400 font-normal ml-1">(non modifiable)</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="email"
                    value={user.email}
                    disabled
                    className="pl-12 h-12 bg-gray-100"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro de téléphone WhatsApp
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="tel"
                    placeholder="+225 07 XX XX XX XX"
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    className="pl-12 h-12"
                    data-testid="phone-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Utilisé pour les notifications WhatsApp
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse
                </label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Votre adresse"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="pl-12 h-12"
                    data-testid="address-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-teal-600 hover:bg-teal-700"
                data-testid="save-btn"
              >
                <Save className="w-5 h-5 mr-2" />
                {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="mt-6 flex justify-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="text-gray-600"
            data-testid="home-btn"
          >
            <Home className="w-4 h-4 mr-2" />
            Accueil
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(getDashboardRoute())}
            className="text-teal-600 border-teal-600"
            data-testid="dashboard-btn"
          >
            Mon tableau de bord
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePortal;
